import * as React from 'react';
import { Tooltip } from 'antd';
import {
  UnitSpan,
  LabelText,
  ThresholdInputWrapper,
  StyledInput,
  ResetToConviva,
  ResetBtn,
  InputContainer,
} from './styled';
import { humanizeValue } from '@components/noc-metric-panel/MetricTable/common/utils';

interface IProps {
  labelAttributes?: { prefix: string; suffix: string };
  enableConviva?: boolean;
  cellData?: {
    metric?: { key: string; unit: string; metrics?: string[] };
    data?: { conviva: string; custom: string; isCustomSelected: boolean };
    identifier?: string;
    unit?: string;
  };
  error?: string;
  handleChange: (valueObj: any) => void;
  disableCustom: 'notAdmin' | 'instantFilter' | 'disableThreshold' | '';
  type: 'threshold_baseline' | 'thresholds';
}

const msg = {
  disableThreshold: 'Your thresholds have been disabled',
  notAdmin: 'Only Admins can edit the Threshold',
  instantFilter: 'Custom thresholds are supported only for Saved Filters',
};

function formatNumber(
  metric: string | undefined,
  value: string | number | undefined | null,
  unit: string = '',
) {
  if (value === undefined || value === null || value === '') {
    return {
      humanizedNumber: '',
      humanizedUnit: unit,
    };
  }
  const num = metric === 'Bitrate' ? Number(value) * 1024 : Number(value);
  return humanizeValue(num, unit);
}

const ThresholdInput = ({
  cellData,
  error,
  labelAttributes,
  disableCustom,
  handleChange,
  type,
}: IProps) => {
  const inputRef = React.useRef(null);
  const [editing, setEditing] = React.useState(false);
  const { conviva, custom, isCustomSelected } = cellData?.data || {};
  const value = isCustomSelected ? custom : conviva;
  const disabled = !!disableCustom;

  const changeHandler = (data) => {
    handleChange({
      type,
      metric: { ...cellData?.metric, unit: cellData?.unit },
      config: {
        identifier: cellData?.identifier || cellData?.metric?.key,
        metric: cellData?.metric?.key,
        data,
      },
    });
  };

  const handleCustomChange = (e) => {
    changeHandler({ custom: e.target.value, isCustomSelected: true });
  };

  const resetToConviva = () => {
    changeHandler({ isCustomSelected: false });
  };

  const { humanizedNumber, humanizedUnit } = formatNumber(
    cellData?.metric?.key,
    value,
    cellData?.unit,
  );

  return (
    <div>
      {labelAttributes?.prefix && <LabelText>{labelAttributes.prefix}</LabelText>}
      <InputContainer ref={inputRef}>
        <Tooltip
          title={error}
          placement="right"
          open={!!error}
          getPopupContainer={() => inputRef.current || document.body}
        >
          <ThresholdInputWrapper $error={!!error}>
            {cellData?.data?.isCustomSelected && (
              <Tooltip title={!disabled && 'Reset to Conviva Threshold'}>
                <ResetBtn
                  type="text"
                  disabled={disabled}
                  icon={<ResetToConviva rotate={90} $disabled={disabled} />}
                  onClick={resetToConviva}
                />
              </Tooltip>
            )}
            <Tooltip title={msg[disableCustom]}>
              <span>
                <StyledInput
                  bordered={false}
                  value={editing ? value : humanizedNumber}
                  onChange={handleCustomChange}
                  disabled={disabled}
                  onFocus={() => setEditing(true)}
                  onBlur={() => setEditing(false)}
                />
              </span>
            </Tooltip>
            {humanizedUnit && !editing && <UnitSpan>{humanizedUnit}</UnitSpan>}
            {cellData?.unit && editing && (
              <UnitSpan>{cellData.unit === 'bps' ? 'Kbps' : cellData.unit}</UnitSpan>
            )}
          </ThresholdInputWrapper>
        </Tooltip>
        {labelAttributes?.suffix && <LabelText>{labelAttributes.suffix}</LabelText>}
      </InputContainer>
    </div>
  );
};

export default React.memo(ThresholdInput);
